import { BASE_API_URL, BASE_PUBLIC_API_ROUTE, IS_LOBBIE_DEV } from "src/constants";

// Defines interfaces for form data
export interface ILocationInfo {
    address: string;
    city: string;
    state: string;
    zip: string;
}

export interface IPatientInfo extends ILocationInfo {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    email: string;
    phone: string;

    address2?: string;
}

export interface IPaymentInfo {
    creditCardNumber: string;
    expirationDate: string;
    cvv: string;
}

export interface IPrefillFieldsInfo {
    [key: string]: string;
}

// Define interfaces for the conversion data structure
export interface ILocationAddress {
    address1: string;
    address2?: string;
    city: string;
    postalCode: string;
    stateProvince: string;
    fullAddress: string;
}

export interface IService {
    id: number;
    careServiceId: number;
    careServiceName: string;
    costInCents: number;
    requiresLab: boolean;
}

export interface IConversion {
    conversionTrackingUuid: string;
    selfSchedulingLink: string;
    locationName: string;
    locationAddress: ILocationAddress;
    practitionerName: string;
    practitionerImageUrl: string;
    services: IService[];
}

export interface IOrderDetails {
    submitData: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        address2?: string;
        city: string;
        state: string;
        zip: string;
    };
    services: {
        id: string;
        careServiceName: string;
        costInCents: number;
        requiresLab: boolean;
    }[];
}

export type FieldType = "checkbox" | "select" | "text" | "radio";
export interface IMedicalQuestion {
    name: string;
    field: string;
    type: FieldType;
    options?: string[];
    required?: boolean;
    dependsOn?: {
        field: string;
        value: string;
    };
}

export interface IMedicalQuestionData {
    [key: string]: string;
}

export const MEDICAL_QUESTIONS: IMedicalQuestion[] = [
    {
        name: "Do you have a history of Type 1 Diabetes?",
        field: "mh_type_2_diabetes",
        type: "radio",
        options: ["Yes", "No"],
    },
    {
        name: "Have you been diagnosed with Type 2 Diabetes?",
        field: "mh_pre_diabetes",
        type: "radio",
        options: ["Yes", "No"],
        dependsOn: { field: "mh_type_2_diabetes", value: "Yes" },
    },
    {
        name: "Have you been diagnosed with obesity (BMI ≥30) or overweight (BMI ≥27) with a weight-related condition?",
        field: "mh_obesity",
        type: "radio",
        options: ["Yes", "No"],
        dependsOn: { field: "mh_type_2_diabetes", value: "Yes" },
    },
    {
        name: "Have you been diagnosed with pancreatitis?",
        field: "mh_pancreatitis",
        type: "select",
        options: ["Yes", "No"],
    },
    {
        name: "Do you have a personal or family history of Multiple Endocrine Neoplasia syndrome type 2 (MEN 2)?",
        field: "mh_men_2",
        type: "select",
        options: ["Yes", "No"],
    },
    {
        name: "Have you ever had severe hypoglycemia (low blood sugar requiring intervention)?",
        field: "mh_hypoglycemia",
        type: "select",
        options: ["Yes", "No"],
    },
    {
        name: "Do you have a history of liver disease?",
        field: "mh_liver_disease",
        type: "select",
        options: ["Yes", "No"],
    },
    {
        name: "Have you ever had gallbladder disease or gallstones?",
        field: "mh_gbd_gallstones",
        type: "select",
        options: ["Yes", "No"],
    },
    {
        name: "Do you have a personal or family history of medullary thyroid carcinoma (MTC)?",
        field: "mh_mtc",
        type: "select",
        options: ["Yes", "No"],
    },
    {
        name: "Do you consume alcohol?",
        field: "ld_alcohol",
        type: "radio",
        options: ["Yes", "No"],
    },
    {
        name: "How often do you consume alcohol?",
        field: "ld_alcohol_frequency",
        type: "select",
        options: ["Once a month", "2-3 times per month", "Weekly", "2-3 times per week", "Daily"],
        dependsOn: { field: "ld_alcohol", value: "Yes" },
    },
    {
        name: "How many drinks per occasion?",
        field: "ld_alcohol_drinks_per_occasion",
        type: "select",
        options: ["1-2 drinks", "3-4 drinks", "5+ drinks"],
        dependsOn: { field: "ld_alcohol", value: "Yes" },
    },
    {
        name: "Do you use tobacco products?",
        field: "ld_tobacco",
        type: "radio",
        options: ["Yes", "No"],
    },
    {
        name: "How often do you exercise?",
        field: "ld_physical_activity",
        type: "select",
        options: ["Rarely", "Once a week", "2-3 times per week", "4-5 times per week", "Daily"],
    },
    {
        name: "What type of diet do you follow?",
        field: "ld_daily_diet",
        type: "select",
        options: [
            "Balanced diet",
            "Vegetarian",
            "Vegan",
            "Keto",
            "Low-carb",
            "Mediterranean",
            "Other (specify)",
        ],
    },
    {
        name: "If other diet, please specify",
        field: "ld_diet_other",
        type: "text",
        dependsOn: { field: "ld_daily_diet", value: "Other (specify)" },
    },
];

// Settings interface
export interface RemoteCareDemoSettings {
    apiPrefix: string;
    apiKey: string;
}

// Default settings
export const DEFAULT_SETTINGS: RemoteCareDemoSettings = {
    apiPrefix: `${BASE_API_URL}/${BASE_PUBLIC_API_ROUTE}/remote-care`,
    apiKey: IS_LOBBIE_DEV
        ? "01957d0a-824e-7098-8e92-a4988112d366" // for Gameday - Embeddables remote care group
        : "68daa98b-184d-47c2-9919-2eed2f5f48b5", // Example API key for seeded data
};

// Local storage key
export const SETTINGS_STORAGE_KEY = "lobbie-telehealth-demo-settings";

export const getRemoteCareDemoSettings = (): RemoteCareDemoSettings => {
    try {
        const storedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
        if (storedSettings) {
            return JSON.parse(storedSettings);
        }
    } catch (err) {
        console.error("Error loading settings from local storage:", err);
    }
    return DEFAULT_SETTINGS;
};

export const getApiUrl = (endpoint: string): string => {
    const { apiPrefix } = getRemoteCareDemoSettings();
    const cleanPrefix = apiPrefix.endsWith("/") ? apiPrefix.slice(0, -1) : apiPrefix;
    const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
    return `${cleanPrefix}${cleanEndpoint}`;
};

// Function to get API headers with auth
export const getApiHeaders = (): Record<string, string> => {
    const { apiKey } = getRemoteCareDemoSettings();
    return {
        Authorization: apiKey,
        "Content-Type": "application/json",
    };
};
